import React, { useState, useEffect, useMemo } from "react";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import Header from "./components/Header";
import ImageSlider from "./components/ImageSlider";
import ThumbnailSlider from "./components/ThumbnailSlider";
import ErrorDisplay from "./components/ErrorDisplay";
import { searchImages } from "./services/adobeStockAPI";
import AmazonProductSlider from "./components/AmazonProductSlider";

const App = () => {
  const [images, setImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [orientation, setOrientation] = useState("horizontal");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  const [mainSwiper, setMainSwiper] = useState(null);
  const [thumbnailSwiper, setThumbnailSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isAuthPanelOpen, setIsAuthPanelOpen] = useState(false);

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isSignUpMode, setIsSignUpMode] = useState(false);

  const [showCollection, setShowCollection] = useState(false);
  const [collectionImages, setCollectionImages] = useState([]);
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState("");
  const [addedImages, setAddedImages] = useState({});

  const [sharedCollections, setSharedCollections] = useState([]);
  const [isViewingSharedCollection, setIsViewingSharedCollection] =
    useState(false);

  const apiKey = process.env.REACT_APP_ADOBE_STOCK_API_KEY;
  const resultsPerPage = 32;

  const stabilizedCollections = useMemo(() => collections, [collections]);

  // Inside App component, add this function:
  const resetToSearchView = () => {
    setShowCollection(false); // Hide collection view
    setIsViewingSharedCollection(false); // Ensure shared view is off
    setImages([]); // Clear any existing search results
    // Optionally clear the search query too for a truly fresh start:
    // setSearchQuery("");
    setHasSearched(false); // Show the initial "Find Your Inspiration" screen
    setPage(1); // Reset pagination
    setTotalResults(0);
    setError(null); // Clear any errors
    setActiveCollection(""); // Deselect any active collection
    setActiveIndex(0); // Reset slider index
    // Optionally reset swiper instances if needed, though re-keying might handle this
    if (mainSwiper && !mainSwiper.destroyed) mainSwiper.slideTo(0, 0);
    if (thumbnailSwiper && !thumbnailSwiper.destroyed)
      thumbnailSwiper.slideTo(0, 0);
    closePanel(); // Close side panels if open
    closeAuthPanel();
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          await currentUser.getIdToken();
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          const adminStatus =
            userDoc.exists() && userDoc.data().isAdmin === true;
          setIsAdmin(adminStatus);
          await fetchCollections(currentUser.uid);
        } catch (err) {
          setError("Failed to initialize user data: " + err.message);
          console.error("Auth State Error:", err);
        }
      } else {
        setIsAdmin(false);
        setCollections([]);
        setCollectionImages([]);
        setActiveCollection("");
        setShowCollection(false);
        setIsViewingSharedCollection(false);
        setImages([]);
        setPage(1);
        setTotalResults(0);
        setHasSearched(false);
        setError(null);
        setAddedImages({});
        closeAuthPanel();
        closePanel();
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchSharedCollections = async () => {
      try {
        const sharedCollectionsRef = collection(db, "sharedCollections");
        const querySnapshot = await getDocs(sharedCollectionsRef);
        const sharedList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSharedCollections(sharedList);
      } catch (err) {
        console.error("Failed to fetch shared collections:", err);
      }
    };
    fetchSharedCollections();
  }, []);

  useEffect(() => {
    if (user) {
      fetchCollections(user.uid);
    }
  }, [user]);

  useEffect(() => {
    if (
      user &&
      showCollection &&
      activeCollection &&
      !isViewingSharedCollection
    ) {
      fetchCollection(user.uid, activeCollection);
    } else if (showCollection && !activeCollection && collections.length > 0) {
      setActiveCollection(collections[0]);
    }
  }, [
    activeCollection,
    showCollection,
    user,
    collections,
    isViewingSharedCollection,
  ]);

  useEffect(() => {
    if (apiKey && !showCollection && hasSearched && searchQuery.trim()) {
      handleSearch(page);
    } else if (!apiKey) {
      setError("API key not configured. Cannot perform search.");
    }
  }, [orientation]);

  useEffect(() => {
    if (mainSwiper && !mainSwiper.destroyed) {
      const handleSlideChange = () => {
        if (
          mainSwiper &&
          !mainSwiper.destroyed &&
          typeof mainSwiper.activeIndex === "number"
        ) {
          setActiveIndex(mainSwiper.activeIndex);
        }
      };
      mainSwiper.on("slideChange", handleSlideChange);
      return () => {
        if (mainSwiper && !mainSwiper.destroyed) {
          mainSwiper.off("slideChange", handleSlideChange);
        }
      };
    }
  }, [mainSwiper]);

  useEffect(() => {
    if (!apiKey) {
      setError(
        "Adobe Stock API key is missing. Please configure REACT_APP_ADOBE_STOCK_API_KEY."
      );
    }
  }, [apiKey]);

  useEffect(() => {
    if (mainSwiper && !mainSwiper.destroyed) {
      mainSwiper.slideTo(0, 0);
    }
    if (thumbnailSwiper && !thumbnailSwiper.destroyed) {
      thumbnailSwiper.slideTo(0, 0);
    }
    setActiveIndex(0);
  }, [showCollection, mainSwiper, thumbnailSwiper]);

  const fetchCollections = async (uid, retries = 3, delay = 1000) => {
    console.log("Fetching collections for UID:", uid);
    console.log("Current user auth state:", auth.currentUser);
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        setError(null);
        const collectionsRef = collection(
          db,
          "collections",
          uid,
          "collections"
        );
        console.log(
          "Attempting to list collections at path:",
          collectionsRef.path
        );
        const querySnapshot = await getDocs(collectionsRef);
        const collectionList = querySnapshot.docs.map((doc) => doc.id).sort();
        console.log("Collections found:", collectionList);
        setCollections(collectionList);

        if (collectionList.length > 0) {
          const firstCollection = collectionList[0];
          if (!activeCollection || !collectionList.includes(activeCollection)) {
            setActiveCollection(firstCollection);
          }
        } else {
          setActiveCollection("");
          setCollectionImages([]);
        }
        return;
      } catch (err) {
        if (err.code === "permission-denied" && attempt < retries) {
          console.log(`Attempt ${attempt} failed, retrying in ${delay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        setError("Failed to load collections: " + err.message);
        console.error("Fetch Collections Error:", err);
        return;
      }
    }
  };

  const fetchCollection = async (uid, collectionName) => {
    if (!uid || !collectionName) {
      setCollectionImages([]);
      setIsViewingSharedCollection(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    setIsViewingSharedCollection(false);
    try {
      const imagesRef = collection(
        db,
        "collections",
        uid,
        "collections",
        collectionName,
        "images"
      );
      const querySnapshot = await getDocs(imagesRef);
      const collectedImages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCollectionImages(collectedImages || []);
      setActiveIndex(0);
      if (mainSwiper && !mainSwiper.destroyed) mainSwiper.slideTo(0, 0);
    } catch (err) {
      setError(`Failed to load collection "${collectionName}": ${err.message}`);
      console.error("Fetch Collection Images Error:", err);
      setCollectionImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSharedCollection = async (sharedCollection) => {
    setIsLoading(true);
    setError(null);
    try {
      setCollectionImages(sharedCollection.images || []);
      setActiveCollection(sharedCollection.collectionName);
      setShowCollection(true);
      setHasSearched(true);
      setIsViewingSharedCollection(true);
      setActiveIndex(0);
      if (mainSwiper && !mainSwiper.destroyed) mainSwiper.slideTo(0, 0);
    } catch (err) {
      setError(`Failed to load shared collection: ${err.message}`);
      console.error("Fetch Shared Collection Error:", err);
      setCollectionImages([]);
      setIsViewingSharedCollection(false);
    } finally {
      setIsLoading(false);
    }
  };

  const shareCollection = async (collectionName) => {
    if (!user || !isAdmin) {
      setError("You must be an admin to share collections.");
      return;
    }
    try {
      const imagesRef = collection(
        db,
        "collections",
        user.uid,
        "collections",
        collectionName,
        "images"
      );
      const querySnapshot = await getDocs(imagesRef);
      const imagesToShare = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const sharedCollectionId = `${user.uid}_${collectionName}`;
      const sharedCollectionRef = doc(
        db,
        "sharedCollections",
        sharedCollectionId
      );
      await setDoc(sharedCollectionRef, {
        adminUid: user.uid,
        adminName: user.displayName || "Admin",
        collectionName,
        images: imagesToShare,
      });

      setSharedCollections((prev) => [
        ...prev,
        {
          id: sharedCollectionId,
          adminUid: user.uid,
          adminName: user.displayName || "Admin",
          collectionName,
          images: imagesToShare,
        },
      ]);
    } catch (err) {
      setError(`Failed to share collection: ${err.message}`);
      console.error("Share Collection Error:", err);
    }
  };

  const unshareCollection = async (sharedCollectionId) => {
    if (!user || !isAdmin) {
      setError("You must be an admin to unshare collections.");
      return;
    }
    try {
      const sharedCollectionRef = doc(
        db,
        "sharedCollections",
        sharedCollectionId
      );
      await deleteDoc(sharedCollectionRef);

      setSharedCollections((prev) =>
        prev.filter((col) => col.id !== sharedCollectionId)
      );
    } catch (err) {
      setError(`Failed to unshare collection: ${err.message}`);
      console.error("Unshare Collection Error:", err);
    }
  };

  const handleSearch = async (targetPage) => {
    if (!searchQuery.trim()) {
      setError("Please enter a search term");
      return;
    }
    if (!apiKey) {
      setError("API key not configured. Cannot perform search.");
      return;
    }
    if (showCollection) {
      setShowCollection(false);
      setIsViewingSharedCollection(false);
    }

    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    setPage(targetPage);

    try {
      const data = await searchImages(
        apiKey,
        searchQuery,
        targetPage,
        orientation
      );

      if (!data || typeof data.nb_results === "undefined") {
        throw new Error(
          "Invalid API response format received from Adobe Stock."
        );
      }
      const newImages = data.files || [];
      setImages(newImages);
      setTotalResults(data.nb_results);
      setActiveIndex(0);
      if (mainSwiper && !mainSwiper.destroyed) mainSwiper.slideTo(0, 0);
    } catch (err) {
      setError(`Search failed: ${err.message}`);
      console.error("API Search Error:", err);
      setImages([]);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCentralFormSubmit = (event) => {
    event.preventDefault();
    handleSearch(1);
  };

  const togglePanel = () => setIsPanelOpen((prev) => !prev);
  const closePanel = () => setIsPanelOpen(false);
  const toggleAuthPanel = () => {
    setIsAuthPanelOpen((prev) => !prev);
    if (!isAuthPanelOpen) {
      setIsSignUpMode(false);
      setEmail("");
      setPassword("");
      setName("");
      setError(null);
    }
  };
  const closeAuthPanel = () => {
    setIsAuthPanelOpen(false);
    setIsSignUpMode(false);
    setEmail("");
    setPassword("");
    setName("");
  };

  const handleSignUp = async () => {
    if (!name.trim()) {
      setError("Name is required for sign-up");
      return;
    }
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const newUser = userCredential.user;
      await updateProfile(newUser, { displayName: name });

      const userDocRef = doc(db, "users", newUser.uid);
      await setDoc(userDocRef, {
        isAdmin: false,
        displayName: name,
      });

      const favoritesCollectionRef = doc(
        db,
        "collections",
        newUser.uid,
        "collections",
        "Favorites"
      );
      await setDoc(favoritesCollectionRef, {
        createdAt: new Date().toISOString(),
      });
      closeAuthPanel();
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("This email address is already registered.");
      } else if (err.code === "auth/weak-password") {
        setError("Password is too weak (at least 6 characters).");
      } else {
        setError(`Sign up failed: ${err.message}`);
      }
      console.error("Sign up error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      closeAuthPanel();
    } catch (err) {
      if (
        err.code === "auth/user-not-found" ||
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-credential"
      ) {
        setError("Invalid email or password.");
      } else {
        setError(`Sign in failed: ${err.message}`);
      }
      console.error("Sign in error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setError(null);
    try {
      await signOut(auth);
    } catch (err) {
      setError(`Sign out failed: ${err.message}`);
      console.error("Sign out error:", err);
    }
  };

  const addToCollection = async (image, collectionName) => {
    if (!user) {
      setError("You must be signed in to add images.");
      toggleAuthPanel();
      return;
    }
    if (!collectionName) {
      setError("Please select a collection.");
      return;
    }
    setError(null);
    try {
      if (!image || typeof image.id === "undefined") {
        throw new Error("Invalid image data.");
      }
      const imageIdString = image.id.toString();
      const collectionRef = doc(
        db,
        "collections",
        user.uid,
        "collections",
        collectionName,
        "images",
        imageIdString
      );
      const imageToSave = {
        title: image.title || "Untitled",
        thumbnail_url: image.thumbnail_url || image.thumbnail_1000_url || "",
        savedAt: new Date().toISOString(),
        adobeStockId: image.id,
        width: image.width,
        height: image.height,
      };
      if (!imageToSave.thumbnail_url) {
        console.warn(
          "Attempting to save image without a thumbnail URL:",
          image
        );
      }
      await setDoc(collectionRef, imageToSave);
      setAddedImages((prev) => ({
        ...prev,
        [imageIdString]: {
          ...prev[imageIdString],
          [collectionName]: true,
        },
      }));
      if (activeCollection === collectionName && showCollection) {
        setCollectionImages((prev) => {
          if (prev.some((img) => img.id === imageIdString)) return prev;
          return [...prev, { id: imageIdString, ...imageToSave }];
        });
      }
    } catch (err) {
      setError(`Failed to add image to "${collectionName}": ${err.message}`);
      console.error("Add to Collection Error:", err);
    }
  };

  const removeFromCollection = async (imageId) => {
    if (!user || !activeCollection) {
      setError(
        "Cannot remove image: No active collection or user not logged in."
      );
      return;
    }
    if (typeof imageId === "undefined" || imageId === null) {
      setError("Cannot remove image: Invalid image ID.");
      return;
    }
    setError(null);
    const imageIdString = String(imageId);
    const originalImages = [...collectionImages];
    setCollectionImages((prevImages) =>
      prevImages.filter((image) => image.id !== imageIdString)
    );
    try {
      const imageRef = doc(
        db,
        "collections",
        user.uid,
        "collections",
        activeCollection,
        "images",
        imageIdString
      );
      await deleteDoc(imageRef);
      setAddedImages((prev) => {
        const updated = { ...prev };
        if (updated[imageIdString]) {
          delete updated[imageIdString][activeCollection];
          if (Object.keys(updated[imageIdString]).length === 0) {
            delete updated[imageIdString];
          }
        }
        return updated;
      });
    } catch (err) {
      setError(`Failed to remove image: ${err.message}`);
      console.error("Remove From Collection Error:", err);
      setCollectionImages(originalImages);
    }
  };

  const createCollection = async (collectionNameInput) => {
    const trimmedName = collectionNameInput.trim();
    if (!user) {
      setError("You must be signed in to create collections.");
      toggleAuthPanel();
      return;
    }
    if (!trimmedName) {
      setError("Collection name cannot be empty.");
      return;
    }
    if (
      collections.some(
        (name) => name.toLowerCase() === trimmedName.toLowerCase()
      )
    ) {
      setError(`Collection "${trimmedName}" already exists.`);
      return;
    }
    setError(null);
    try {
      const collectionRef = doc(
        db,
        "collections",
        user.uid,
        "collections",
        trimmedName
      );
      await setDoc(collectionRef, { createdAt: new Date().toISOString() });
      setCollections((prev) => [...prev, trimmedName].sort());
      setActiveCollection(trimmedName);
      setShowCollection(true);
      setCollectionImages([]);
      setHasSearched(true);
    } catch (err) {
      setError(`Failed to create collection: ${err.message}`);
      console.error("Create Collection Error:", err);
    }
  };

  const deleteCollection = async (collectionName) => {
    if (!user) {
      setError("You must be signed in.");
      toggleAuthPanel();
      return;
    }
    if (collectionName === "Favorites") {
      setError("The 'Favorites' collection cannot be deleted.");
      return;
    }
    if (!collectionName || !collections.includes(collectionName)) {
      setError("Invalid collection.");
      return;
    }
    setError(null);
    try {
      const collectionRef = doc(
        db,
        "collections",
        user.uid,
        "collections",
        collectionName
      );
      const imagesRef = collection(collectionRef, "images");
      const qSnap = await getDocs(imagesRef);
      const deletePromises = qSnap.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(deletePromises);
      await deleteDoc(collectionRef);
      const remainingCols = collections.filter((c) => c !== collectionName);
      setCollections(remainingCols);
      setAddedImages((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((imageId) => {
          if (updated[imageId][collectionName]) {
            delete updated[imageId][collectionName];
            if (Object.keys(updated[imageId]).length === 0) {
              delete updated[imageId];
            }
          }
        });
        return updated;
      });
      const sharedCollectionId = `${user.uid}_${collectionName}`;
      if (sharedCollections.some((col) => col.id === sharedCollectionId)) {
        await unshareCollection(sharedCollectionId);
      }
      if (activeCollection === collectionName) {
        if (remainingCols.length > 0) {
          const newActive = remainingCols.includes("Favorites")
            ? "Favorites"
            : remainingCols[0];
          setActiveCollection(newActive);
          if (showCollection) {
            fetchCollection(user.uid, newActive);
          }
        } else {
          setActiveCollection("");
          setShowCollection(false);
          setIsViewingSharedCollection(false);
          setCollectionImages([]);
          setHasSearched(false);
        }
      }
    } catch (err) {
      setError(`Failed to delete collection: ${err.message}`);
      console.error("Delete Collection Error:", err);
    }
  };

  const handleSetActiveCollection = (collectionName) => {
    setActiveCollection(collectionName);
    setIsViewingSharedCollection(false); // Reset when selecting a new collection
    if (!showCollection && collectionName) {
      setShowCollection(true);
      setHasSearched(true);
      setError(null);
    }
  };

  const handleToggleCollectionView = (show) => {
    setError(null);
    if (show !== showCollection) {
      if (show) {
        if (collections.length === 0 && user) {
          createCollection("Favorites");
          setHasSearched(true);
          return;
        }
        if (collections.length === 0 && !user) {
          setError("Please sign in to view or create collections.");
          toggleAuthPanel();
          return;
        }
        if (!activeCollection && collections.length > 0) {
          setActiveCollection(collections[0]);
        }
        setShowCollection(true);
        setHasSearched(true);
      } else {
        setShowCollection(false);
        setIsViewingSharedCollection(false);
      }
    }
  };

  const currentImages = showCollection ? collectionImages : images;
  const totalPages = Math.ceil(totalResults / resultsPerPage);

  const renderPagination = () => {
    if (showCollection || totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, page - halfRange);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="mt-4 flex justify-center items-center gap-2">
        <button
          onClick={() => handleSearch(page - 1)}
          disabled={page === 1 || isLoading}
          className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:opacity-50"
        >
          Prev
        </button>
        {startPage > 1 && (
          <>
            <button
              onClick={() => handleSearch(1)}
              className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              1
            </button>
            {startPage > 2 && <span>...</span>}
          </>
        )}
        {pageNumbers.map((num) => (
          <button
            key={num}
            onClick={() => handleSearch(num)}
            className={`px-3 py-1 rounded ${
              num === page
                ? "bg-blue-600 text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            }`}
            disabled={isLoading}
          >
            {num}
          </button>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span>...</span>}
            <button
              onClick={() => handleSearch(totalPages)}
              className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              {totalPages}
            </button>
          </>
        )}
        <button
          onClick={() => handleSearch(page + 1)}
          disabled={page === totalPages || isLoading}
          className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  const isAuthError =
    error &&
    (error.includes("Sign in") ||
      error.includes("Sign up") ||
      error.includes("Invalid") ||
      error.includes("required") ||
      error.includes("registered") ||
      error.includes("weak") ||
      error.includes("must be signed in") ||
      error.includes("reset") || // Add this to include password reset messages
      error.includes("No user found") || // Include "No user found with this email address."
      error.includes("Password reset email sent")); // Include success message

  const generalError = error && !isAuthError;

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
      <Header
        query={searchQuery}
        setQuery={setSearchQuery}
        orientation={orientation}
        setOrientation={setOrientation}
        onSearch={() => handleSearch(1)}
        isLoading={isLoading}
        isPanelOpen={isPanelOpen}
        togglePanel={togglePanel}
        closePanel={closePanel}
        user={user}
        isAdmin={isAdmin}
        isAuthPanelOpen={isAuthPanelOpen}
        toggleAuthPanel={toggleAuthPanel}
        closeAuthPanel={closeAuthPanel}
        handleSignIn={handleSignIn}
        handleSignUp={handleSignUp}
        handleSignOut={handleSignOut}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        name={name}
        setName={setName}
        isSignUpMode={isSignUpMode}
        setIsSignUpMode={setIsSignUpMode}
        authError={isAuthError ? error : null}
        setError={setError} // Add this line to pass setError as a prop
        showCollection={showCollection}
        setShowCollection={handleToggleCollectionView}
        collections={stabilizedCollections}
        activeCollection={activeCollection}
        setActiveCollection={handleSetActiveCollection}
        createCollection={createCollection}
        deleteCollection={deleteCollection}
        sharedCollections={sharedCollections}
        fetchSharedCollection={fetchSharedCollection}
        shareCollection={shareCollection}
        unshareCollection={unshareCollection}
        resetToSearchView={resetToSearchView}
      />

      <main className="container mx-auto px-4 py-8 flex-grow flex flex-col">
        <ErrorDisplay error={generalError ? error : null} />
        <div className="mb-4 text-center text-gray-700 dark:text-gray-300 min-h-[1.5em]">
          {isLoading
            ? "Loading..."
            : showCollection
            ? `Viewing "${activeCollection}" collection.`
            : hasSearched && !showCollection && images.length > 0
            ? `Search results for "${searchQuery}" (${orientation}), Page ${page} of ${totalPages}.`
            : ""}
        </div>
        {isLoading ? (
          <div className="flex-grow flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : !showCollection && !hasSearched ? (
          <div className="flex-grow flex flex-col justify-center items-center">
            <h2 className="text-3xl font-semibold text-center text-gray-700 dark:text-gray-300 mb-6">
              Find Your Inspiration{" "}
              <span className="block text-sm font-medium">
                Search results are displayed in a timed slideshow.
              </span>
            </h2>
            {!user && (
              <p
                className="text-l font-semibold text-gray-700 dark:text-gray-300 dark:hover:text-white mb-6 cursor-pointer"
                onClick={toggleAuthPanel}
              >
                Draw from search results or sign in and create custom
                collections.
              </p>
            )}
            <form
              onSubmit={handleCentralFormSubmit}
              className="w-full max-w-lg flex flex-col md:flex-row items-center gap-2 mb-8"
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Adobe Stock..."
                className="flex-grow w-full md:w-auto px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full md:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                aria-label="Search"
              >
                <svg
                  className="w-5 h-5 m-auto"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  fill="currentColor"
                >
                  <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
                </svg>
              </button>
            </form>
            {sharedCollections.length > 0 && (
              <div className="mt-6 w-full max-w-4xl border border-gray-700 rounded p-4 text-center">
                <div></div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Curated Collection Examples
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                  These collections of action based images are great for
                  studying movement and are fun gesture drawing references.
                </p>
                {!user && (
                  <strong
                    className="text-sm text-gray-700 dark:text-gray-300 block mb-4 cursor-pointer dark:hover:text-white"
                    onClick={toggleAuthPanel}
                  >
                    Sign In to create and save your own collections.
                  </strong>
                )}

                <ul className="flex gap-2 items-stretch justify-center min-h-[1px]">
                  {sharedCollections.map((sharedCol) => (
                    <li
                      key={sharedCol.id}
                      className="flex-grow flex flex-col text-center justify-between items-center p-2 bg-gray-200 dark:bg-gray-700 rounded-lg flex-1 mt-0 w-[33%]"
                    >
                      <div
                        onClick={() => fetchSharedCollection(sharedCol)}
                        className="cursor-pointer max-h-[50px] md:max-h-[150px] overflow-hidden flex items-center rounded mb-2"
                      >
                        <img
                          src={sharedCol.images[0].thumbnail_url} // Correct path: sharedCol.images[0]
                          alt={`Preview of ${sharedCol.collectionName}`}
                          className="object-cover" // Added basic styling
                        />
                      </div>

                      <span className="block text-sm sample-collection-name mb-2 dark:text-gray-300">
                        {sharedCol.collectionName}
                      </span>
                      <button
                        onClick={() => fetchSharedCollection(sharedCol)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : currentImages.length === 0 ? (
          <div className="flex-grow flex justify-center items-center text-center text-gray-600 dark:text-gray-300">
            {showCollection ? (
              <p>
                Your "{activeCollection}" collection is empty. <br />
                Add images from{" "}
                <button
                  onClick={resetToSearchView}
                  className="text-blue-500 hover:underline"
                >
                  Search
                </button>
                .
              </p>
            ) : hasSearched ? (
              <p>
                No results found for "{searchQuery}" ({orientation}). Try
                different keywords?
              </p>
            ) : (
              <p>Enter a search term.</p>
            )}
          </div>
        ) : (
          <>
            <ImageSlider
              key={
                showCollection
                  ? `collection-${activeCollection}`
                  : `search-results-page-${page}`
              }
              images={currentImages}
              onSwiper={setMainSwiper}
              thumbnailSwiper={thumbnailSwiper}
              isPanelOpen={isPanelOpen}
              user={user}
              addToCollection={addToCollection}
              removeFromCollection={
                showCollection ? removeFromCollection : null
              }
              showCollection={showCollection}
              collections={stabilizedCollections}
              activeCollection={activeCollection}
              initialSlide={activeIndex}
              addedImages={addedImages}
              isAdmin={isAdmin}
              isSharedCollection={showCollection && isViewingSharedCollection}
            />
            {mainSwiper && currentImages.length > 1 && (
              <ThumbnailSlider
                key={
                  showCollection
                    ? `collection-thumbs-${activeCollection}`
                    : `search-thumbs-page-${page}`
                }
                images={currentImages}
                mainSwiper={mainSwiper}
                activeIndex={activeIndex}
                onSwiper={setThumbnailSwiper}
                initialSlide={activeIndex}
              />
            )}
            {renderPagination()}
          </>
        )}
      </main>
      <footer className="w-full">
        <AmazonProductSlider />
      </footer>
    </div>
  );
};

export default App;
